import type { TFunction } from 'i18next'

import { ApiError, friendlyErrorKey } from '@/lib/api'
import { currentLocale } from '@/lib/i18n'
import { formatDate, formatTime } from '@/lib/i18n/format'

import type {
  ActivityChatSummary,
  ActivityMessage,
  PrivateConversation,
  PrivateMessage,
} from './api'

export type ChatListItem =
  | {
      kind: 'activity'
      id: string
      pinned: boolean
      lastMessageAt: string | null
      chat: ActivityChatSummary
    }
  | {
      kind: 'private'
      id: string
      pinned: boolean
      lastMessageAt: string | null
      conversation: PrivateConversation
    }

function timestamp(value: string | null): number | null {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

function compareTimestampDesc(a: string | null, b: string | null): number {
  const left = timestamp(a)
  const right = timestamp(b)
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1
  return right - left
}

/**
 * Merge the two frozen lists into one active conversation list. Pinned rows
 * first, then latest message (null last), matching UI_PARITY §3.12.
 * Archived activity chats are returned separately for the "past activities"
 * expansion.
 */
export function splitChatList(
  activityChats: ActivityChatSummary[],
  privateChats: PrivateConversation[],
): { active: ChatListItem[]; archived: ActivityChatSummary[] } {
  const archived = activityChats.filter((chat) => chat.archived)
  const active: ChatListItem[] = [
    ...privateChats.map<ChatListItem>((conversation) => ({
      kind: 'private',
      id: conversation.id,
      pinned: conversation.pinned,
      lastMessageAt: conversation.lastMessageAt,
      conversation,
    })),
    ...activityChats
      .filter((chat) => !chat.archived)
      .map<ChatListItem>((chat) => ({
        kind: 'activity',
        id: chat.id,
        pinned: chat.pinned,
        lastMessageAt: chat.lastMessageAt,
        chat,
      })),
  ]

  active.sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
    const byTime = compareTimestampDesc(a.lastMessageAt, b.lastMessageAt)
    if (byTime !== 0) return byTime
    return a.id.localeCompare(b.id)
  })

  return { active, archived }
}

export function unreadTotal(
  activityChats: ActivityChatSummary[],
  privateChats: PrivateConversation[],
): number {
  const activity = activityChats.reduce(
    (total, chat) => total + Math.max(0, chat.unreadCount),
    0,
  )
  const privates = privateChats.reduce(
    (total, conversation) => total + Math.max(0, conversation.unreadCount),
    0,
  )
  return activity + privates
}

export function archivedUnreadTotal(archived: ActivityChatSummary[]): number {
  return archived.reduce(
    (total, chat) => total + Math.max(0, chat.unreadCount),
    0,
  )
}

/**
 * The API flags archived chats when the activity is cancelled or its end time
 * has passed. For a deep-linked detail we derive the same state locally.
 */
export function isActivityArchived(
  activity: Pick<ActivityChatSummary, 'status' | 'endAt' | 'startAt'>,
  now = Date.now(),
): boolean {
  if (activity.status === 'cancelled') return true
  const boundary = activity.endAt ?? activity.startAt
  if (!boundary) return false
  const end = Date.parse(boundary)
  return Number.isFinite(end) && end <= now
}

const READ_ONLY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Activity chat writes are accepted until 7 days after `end_at`
 * (`API_CONTRACT.md` §6.16). Cancelled activities follow the same window.
 */
export function isActivityChatReadOnly(
  endAt: string | null,
  now = Date.now(),
): boolean {
  if (!endAt) return false
  const end = Date.parse(endAt)
  return Number.isFinite(end) && now > end + READ_ONLY_WINDOW_MS
}

export function latestActivityMessageId(
  messages: ActivityMessage[],
): string | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message && message.type === 'user_message' && message.id) {
      return message.id
    }
  }
  return null
}

export function latestPrivateMessageId(
  messages: PrivateMessage[],
): string | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message?.id) return message.id
  }
  return null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string'
}

/**
 * Realtime frames carry the same JSON shape as the REST message objects
 * (REALTIME.md §3). Guard before merging so a malformed event falls back to a
 * targeted REST refetch instead of corrupting the timeline.
 */
export function isActivityMessage(value: unknown): value is ActivityMessage {
  if (!isRecord(value)) return false
  return (
    typeof value.id === 'string' &&
    value.id.length > 0 &&
    typeof value.body === 'string' &&
    typeof value.createdAt === 'string' &&
    isNullableString(value.senderId) &&
    typeof value.senderName === 'string' &&
    isNullableString(value.senderAvatarUrl) &&
    typeof value.mine === 'boolean' &&
    (value.type === 'user_message' || value.type === 'system')
  )
}

export function isPrivateMessage(value: unknown): value is PrivateMessage {
  if (!isRecord(value)) return false
  return (
    typeof value.id === 'string' &&
    value.id.length > 0 &&
    typeof value.body === 'string' &&
    typeof value.createdAt === 'string' &&
    typeof value.senderId === 'string' &&
    typeof value.isMine === 'boolean' &&
    typeof value.nickname === 'string' &&
    typeof value.publicName === 'string' &&
    isNullableString(value.avatarUrl)
  )
}

function messageTime(value: { createdAt: string; id: string }): number {
  const parsed = Date.parse(value.createdAt)
  return Number.isFinite(parsed) ? parsed : 0
}

function compareMessages(
  a: { createdAt: string; id: string },
  b: { createdAt: string; id: string },
): number {
  const byTime = messageTime(a) - messageTime(b)
  if (byTime !== 0) return byTime
  return a.id.localeCompare(b.id)
}

function mergeById<T extends { id: string; createdAt: string }>(
  current: T[],
  incoming: T[],
): T[] {
  const byId = new Map<string, T>()
  for (const item of current) byId.set(item.id, item)
  for (const item of incoming) byId.set(item.id, item)
  return [...byId.values()].sort(compareMessages)
}

/** Append a realtime activity message once, ordered like the REST timeline. */
export function mergeActivityMessage(
  current: ActivityMessage[],
  incoming: ActivityMessage,
): ActivityMessage[] {
  return mergeById(current, [incoming])
}

/** Merge an authoritative REST timeline with realtime messages already shown. */
export function mergeActivityTimeline(
  server: ActivityMessage[],
  current: ActivityMessage[],
): ActivityMessage[] {
  return mergeById(server, current)
}

export function mergePrivateMessage(
  current: PrivateMessage[],
  incoming: PrivateMessage,
): PrivateMessage[] {
  return mergeById(current, [incoming])
}

export function mergePrivateTimeline(
  server: PrivateMessage[],
  current: PrivateMessage[],
): PrivateMessage[] {
  return mergeById(server, current)
}

export function privateDisplayName(
  conversation: Pick<
    PrivateConversation,
    'firstName' | 'lastName' | 'publicName' | 'nickname'
  >,
): string {
  const fullName = [conversation.firstName, conversation.lastName]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ')
  return (
    fullName ||
    conversation.publicName.trim() ||
    conversation.nickname.trim()
  )
}

export function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function isYesterday(date: Date, now = new Date()): boolean {
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  return sameLocalDay(date, yesterday)
}

/** Compact list timestamp: time today, "Ieri", numeric date before that. */
export function formatChatTimestamp(
  value: string | null,
  t: TFunction,
  now = new Date(),
): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  if (sameLocalDay(date, now)) return formatTime(date, currentLocale())
  if (isYesterday(date, now)) return t('chat.date.yesterday')
  return formatDate(date, currentLocale())
}

/** Date divider label for the chat timeline. */
export function formatChatDay(
  value: string,
  t: TFunction,
  now = new Date(),
): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  if (sameLocalDay(date, now)) return t('chat.date.today')
  if (isYesterday(date, now)) return t('chat.date.yesterday')
  return formatDate(date, currentLocale())
}

/**
 * Chat-specific error copy. The shared mapper does not know `CHAT_READ_ONLY`
 * and private conversations need a single friendship/block explanation.
 */
export function chatErrorKey(
  error: unknown,
  { privateChat = false }: { privateChat?: boolean } = {},
): string {
  if (error instanceof ApiError) {
    if (error.code === 'CHAT_READ_ONLY') return 'chat.activity.readOnlyError'
    if (privateChat && (error.status === 403 || error.code === 'FORBIDDEN')) {
      return 'chat.private.unavailable'
    }
  }
  return friendlyErrorKey(error)
}

export function maxBadgeCount(count: number): string {
  if (!Number.isFinite(count) || count <= 0) return '0'
  return count > 99 ? '99+' : String(count)
}
